// client/src/components/pages/RouteSummary/TransactionDetailsModal.tsx (converted from .js, ST-00216)
//
// ST-00218 (lm-status): with load matching on, every list shows each ticket's
// "Load status" (the tenant's load-match ledger) after Ticket No. In the Brought /
// Carried Forward lists it replaces the older delivery-no "Receipt Found" /
// "Receipted" column, which is then hidden by default but stays in the column
// picker. With load matching off nothing changes.
//
// ST-00218 P3: on a "Truck + time" route (the bfcf-details answer has `method: 'engine'`)
// the lists come from the load-match ledger: the delivery-no column is not offered at all,
// "Why carried forward" (CF) and "Paired receipt" are shown instead, and four more lists open
// from the summary (received off-route, no receipt data, missing, needs review). On a
// delivery-number route "Receipt Found" / "Receipted" stays visible next to Load status: it
// is what explains that route's figure.
import { useState, useEffect, useMemo, useRef, type MouseEvent, type ReactNode } from 'react';
import {
  FaTimes,
  FaSpinner,
  FaFileExport,
  FaCog,
  FaEye,
  FaEyeSlash,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';
import api from '../../../services/api';
import { MINING_TZ } from '../../../utils/dateUtils';
import { buildTransactionsCsv, cfReasonText } from './transactionCsv';
import { groupingScopeParams } from './routeSummaryParams';
import { useLoadStatuses } from '../../../hooks/useLoadStatuses';
import { LoadStatusCell } from '../../common/LoadStatusBadge';
import type {
  BfcfRequest,
  BfcfVariant,
  GroupingId,
  RouteSummaryRequestError,
  TransactionDetailRow,
  TransactionDetailsResponse,
  TransactionType,
} from '../../../types/clientPages/routeSummary';

export interface TransactionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Per-day mode: the day (YYYY-MM-DD) and side to list. */
  date?: string | null;
  type?: TransactionType | null;
  groupingId?: GroupingId | null;
  groupingIds?: GroupingId[] | null;
  minWeight?: number | null;
  groupingName?: string;
  bfcf?: BfcfRequest | null;
}

interface DetailsColumn {
  key: string;
  label: string;
  defaultVisible: boolean;
}

/** Stored { columnKey: visible } preferences (localStorage). */
type ColumnPreferences = Record<string, boolean | undefined>;

/** ST-00218 P3: why a receipt of the period has no dispatch on the route, in plain words. */
const RECEIPT_REASON_TEXT: Record<string, string> = {
  orphan: 'No dispatch found',
  from_off_route: 'Dispatched on another route',
  no_dispatch_data: 'No dispatch data for that time',
  pending: 'Not matched yet (the ledger is catching up)',
};

/** The window's title and explanation per list. */
const LIST_TEXT: Record<BfcfVariant, { title: string; legacy: string; engine?: string }> = {
  'brought-forward': {
    title: 'Brought Forward Loads',
    legacy:
      'Dispatched before this period but receipted inside it — these are added to the dispatch total.',
  },
  'carried-forward': {
    title: 'Carried Forward Loads',
    legacy:
      'Dispatched in this period with no matching receipt inside it — these are deducted from the dispatch total.',
    engine:
      'Dispatched in this period and not received inside it — received after the period, still in transit, or not in the ledger yet. These are deducted from the dispatch total.',
  },
  'received-off-route': {
    title: 'Received Off-route Loads',
    legacy:
      'Dispatched in this period and received on a site that is not on this route — deducted from the dispatch total, like Carried Forward.',
  },
  'no-receipt-data': {
    title: 'Loads Without Receipt Data',
    legacy:
      'Dispatched in this period while the route has no receipt data for that time — not reconciled, so deducted from the dispatch total.',
  },
  missing: {
    title: 'Missing Loads',
    legacy:
      'Dispatched in this period, not received and past the route’s transit window. They stay in the dispatch total, so the variance shows them.',
  },
  'needs-review': {
    title: 'Loads to Review',
    legacy:
      'The truck was dispatched again before a receipt for this load — check which dispatch is real. They stay in the dispatch total.',
  },
  'unmatched-receipts': {
    title: 'Receipts With No Dispatch',
    legacy:
      'Received in this period with no dispatch on this route — no dispatch found, a load dispatched on another route, or no dispatch data. They stay in the receipt total.',
  },
};

// `bfcf` switches the modal into Brought/Carried Forward mode: instead of one
// day's transactions it lists the dispatch tickets that make up the BF or CF row
// of the summary table. Shape:
//   { variant: 'brought-forward' | 'carried-forward', groupingIds, startDate, endDate, minWeight }
const TransactionDetailsModal = ({
  isOpen,
  onClose,
  date,
  type,
  groupingId,
  // ST-00215 (RSO-6 / RSO-10): per-day mode lists every route of a combined
  // summary, at the summary's Min weight.
  groupingIds = null,
  minWeight,
  groupingName,
  bfcf = null,
}: TransactionDetailsModalProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TransactionDetailsResponse | null>(null);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(500); // Fixed at 500 as per requirements
  const abortControllerRef = useRef<AbortController | null>(null);

  // ST-00218: the Load status labels of the listed tickets; the column shows only
  // when the server answers that load matching is on.
  const loadStatusUids = useMemo(() => (data?.transactions || []).map((t) => t.ticketUID), [data]);
  const {
    enabled: loadStatusEnabled,
    statuses: loadStatuses,
    loading: loadStatusLoading,
  } = useLoadStatuses(loadStatusUids, { skip: !isOpen });
  const showLoadStatus = loadStatusEnabled === true;

  // Nett weight selection state for sum tooltip
  const [selectedWeightIndices, setSelectedWeightIndices] = useState<Set<number>>(new Set());
  const selectedWeightSum = (() => {
    if (!data?.transactions || selectedWeightIndices.size === 0) return 0;
    let sum = 0;
    selectedWeightIndices.forEach((idx) => {
      const tx = data.transactions[idx];
      if (tx?.nettWeight) sum += tx.nettWeight;
    });
    return sum;
  })();

  // Toggle weight cell selection
  const handleWeightCellClick = (e: MouseEvent<HTMLTableCellElement>, index: number) => {
    e.stopPropagation();
    setSelectedWeightIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // Clear selection
  const clearWeightSelection = () => {
    setSelectedWeightIndices(new Set());
  };

  const bfcfVariant = bfcf?.variant || null;
  const isCarriedForward = bfcfVariant === 'carried-forward';
  // ST-00218 P3: a list from the load-match ledger (a Truck + time route).
  const isEngineList = !!bfcfVariant && (data?.method === 'engine' || data?.method === 'mixed');

  // Define all available columns in the EXACT order specified in requirements
  const allColumns: DetailsColumn[] = [
    { key: 'date', label: 'Date', defaultVisible: true },
    { key: 'ticketTimestamp', label: 'Ticket Timestamp', defaultVisible: true },
    { key: 'ticketType', label: 'Ticket Type', defaultVisible: !bfcfVariant },
    { key: 'ticketNo', label: 'Ticket No', defaultVisible: true },
    ...(showLoadStatus ? [{ key: 'loadStatus', label: 'Load status', defaultVisible: true }] : []),
    { key: 'vehicleReg', label: 'Reg No', defaultVisible: true },
    { key: 'dispatchLocation', label: 'Dispatch Location', defaultVisible: true },
    { key: 'receiptLocation', label: 'Receipt Location', defaultVisible: true },
    { key: 'nettWeight', label: 'Nett Weight', defaultVisible: true },
    { key: 'product', label: 'Product', defaultVisible: true },
    // BF/CF of a delivery-number route: does a matching receipt exist, and when?
    // (delivery-no match — what explains that route's figure; shown next to Load status).
    ...(bfcfVariant && !isEngineList
      ? [
          {
            key: 'receiptStatus',
            label: isCarriedForward ? 'Receipt Found' : 'Receipted',
            defaultVisible: true,
          },
        ]
      : []),
    // ST-00218 P3, Truck + time routes: why a load is carried forward, and its paired receipt.
    ...(isEngineList && isCarriedForward
      ? [{ key: 'cfReason', label: 'Why carried forward', defaultVisible: true }]
      : []),
    ...(isEngineList && bfcfVariant === 'unmatched-receipts'
      ? [{ key: 'receiptReason', label: 'Why no dispatch', defaultVisible: true }]
      : []),
    ...(isEngineList
      ? [
          {
            key: 'partnerTicketNo',
            label: bfcfVariant === 'unmatched-receipts' ? 'Paired dispatch' : 'Paired receipt',
            defaultVisible: true,
          },
        ]
      : []),
    // Additional columns (optional, hidden by default)
    { key: 'grossWeight', label: 'First Weight', defaultVisible: false },
    { key: 'tareWeight', label: 'Second Weight', defaultVisible: false },
    { key: 'customer', label: 'Customer', defaultVisible: false },
    { key: 'transporter', label: 'Haulier', defaultVisible: false },
    { key: 'driverName', label: 'Driver', defaultVisible: false },
    { key: 'deliveryNo', label: 'Delivery No', defaultVisible: !!bfcfVariant },
    ...(type === 'receipt'
      ? [{ key: 'variancePercent', label: 'Variance %', defaultVisible: false }]
      : []),
  ];

  // Column visibility is stored as a { key: bool } preference map rather than a
  // materialised column list, so columns that only exist in some modes (BF/CF,
  // receipt variance) still pick up the right default when the mode changes
  // while the modal stays mounted.
  const loadColumnPreferences = (): ColumnPreferences => {
    const stored = localStorage.getItem('transactionDetailsVisibleColumns');
    if (stored) {
      try {
        return JSON.parse(stored) as ColumnPreferences;
      } catch (e) {
        console.error('Error parsing stored column preferences:', e);
      }
    }
    return {};
  };

  const [columnPreferences, setColumnPreferences] = useState(loadColumnPreferences);

  const visibleColumns = allColumns.map((col) => ({
    ...col,
    visible:
      columnPreferences[col.key] !== undefined ? columnPreferences[col.key] : col.defaultVisible,
  }));

  // Save column preferences to localStorage
  const saveColumnPreferences = (preferences: ColumnPreferences) => {
    setColumnPreferences(preferences);
    localStorage.setItem('transactionDetailsVisibleColumns', JSON.stringify(preferences));
  };

  // Toggle column visibility
  const toggleColumn = (columnKey: string) => {
    const current = visibleColumns.find((col) => col.key === columnKey);
    saveColumnPreferences({ ...columnPreferences, [columnKey]: !current!.visible });
  };

  // Reset to defaults — clears the stored preferences rather than freezing the
  // current mode's defaults, so each mode keeps falling back to its own.
  const resetToDefaults = () => {
    saveColumnPreferences({});
  };

  // Get only visible columns
  const getVisibleColumnsOnly = () => visibleColumns.filter((col) => col.visible);

  // Render cell content based on column type
  const renderCellContent = (
    transaction: TransactionDetailRow,
    column: DetailsColumn
  ): ReactNode => {
    switch (column.key) {
      case 'date':
        return formatDate(transaction.ticketTimestamp);
      case 'ticketTimestamp':
        return formatTime(transaction.ticketTimestamp);
      case 'ticketType':
        return transaction.ticketType;
      case 'ticketNo':
        return transaction.ticketNo;
      case 'loadStatus':
        return (
          <LoadStatusCell
            status={transaction.ticketUID ? loadStatuses[transaction.ticketUID] : null}
            loading={loadStatusLoading}
          />
        );
      case 'vehicleReg':
        return transaction.vehicleReg || '-';
      case 'dispatchLocation':
        return transaction.dispatchLocation;
      case 'receiptLocation':
        return transaction.receiptLocation;
      case 'nettWeight':
        return (
          transaction.nettWeight?.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) || '0.00'
        );
      case 'product':
        return transaction.product || '-';
      case 'grossWeight':
        return transaction.grossWeight || '-';
      case 'tareWeight':
        return transaction.tareWeight || '-';
      case 'customer':
        return transaction.customer || '-';
      case 'transporter':
        return transaction.transporter || '-';
      case 'driverName':
        return transaction.driverName || '-';
      case 'deliveryNo':
        return transaction.deliveryNo || '-';
      case 'receiptStatus':
        if (isCarriedForward) {
          return transaction.lateReceiptTimestamp ? (
            <span className="text-amber-700 font-medium">
              Receipted {formatDate(transaction.lateReceiptTimestamp)} (after period)
            </span>
          ) : (
            <span className="text-slate-500">No receipt found</span>
          );
        }
        return transaction.matchedReceiptTimestamp
          ? formatDate(transaction.matchedReceiptTimestamp)
          : '-';
      case 'cfReason':
        return cfReasonText(transaction, formatDate) || '-';
      case 'receiptReason':
        return RECEIPT_REASON_TEXT[transaction.receiptReason || ''] || '-';
      case 'partnerTicketNo':
        return transaction.partnerTicketNo || '-';
      case 'variancePercent':
        return transaction.variancePercent !== null
          ? `${transaction.variancePercent!.toFixed(2)}%`
          : '-';
      default:
        return '-';
    }
  };

  const bfcfGroupingKey = (bfcf?.groupingIds || []).join(',');
  const dayGroupingKey = (groupingIds || []).join(',');

  useEffect(() => {
    if (isOpen && bfcfVariant && bfcfGroupingKey) {
      setCurrentPage(1);
      setSelectedWeightIndices(new Set());
      fetchBfcfDetails();
    } else if (isOpen && !bfcfVariant && date && type && groupingId) {
      setCurrentPage(1); // Reset to first page when modal opens
      setSelectedWeightIndices(new Set()); // Clear weight selection
      fetchTransactionDetails(1);
    }

    // Cleanup on unmount or when modal closes
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    isOpen,
    date,
    type,
    groupingId,
    dayGroupingKey,
    minWeight,
    bfcfVariant,
    bfcfGroupingKey,
    bfcf?.startDate,
    bfcf?.endDate,
    bfcf?.minWeight,
  ]);

  const fetchTransactionDetails = async (page: number = currentPage) => {
    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      console.log('Fetching transaction details with:', {
        groupingId,
        date,
        type,
        page,
        limit: pageSize,
      });

      const response = await api.routeSummary.getTransactionDetails(
        groupingId!,
        {
          date,
          type,
          page,
          limit: pageSize,
          ...(minWeight !== undefined && minWeight !== null ? { minWeight } : {}),
          ...groupingScopeParams(groupingIds),
        },
        { signal: abortControllerRef.current.signal }
      );

      console.log('Transaction details response:', response);
      setData(response.data as TransactionDetailsResponse);
      setCurrentPage(page);
    } catch (caught) {
      const err = caught as RouteSummaryRequestError;
      if (err.name !== 'AbortError') {
        console.error('Error fetching transaction details:', err);
        console.error('Error response:', err.response);

        // Extract error message from response
        let errorMessage = 'Failed to load transaction details';
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        } else if (err.message) {
          errorMessage = err.message;
        }

        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // BF/CF mode: one shot, no pagination — the constituent tickets of a summary
  // row are a small set by nature (they are the loads that failed to match).
  const fetchBfcfDetails = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const response = await api.routeSummary.getBfcfDetails(
        {
          groupingIds: bfcf!.groupingIds,
          startDate: bfcf!.startDate,
          endDate: bfcf!.endDate,
          minWeight: bfcf!.minWeight,
          type: bfcf!.variant,
          // ST-00218 P3: the matching the summary showed (a figure and its list never differ)
          ...(bfcf!.matching ? { matching: bfcf!.matching } : {}),
        },
        { signal: abortControllerRef.current.signal }
      );

      setData({
        ...(response.data as TransactionDetailsResponse),
        pagination: null,
      });
    } catch (caught) {
      const err = caught as RouteSummaryRequestError;
      if (err.name !== 'AbortError') {
        console.error('Error fetching brought/carried forward details:', err);
        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            'Failed to load transaction details'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    onClose();
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= (data?.pagination?.totalPages || 1)) {
      setSelectedWeightIndices(new Set()); // Clear weight selection on page change
      fetchTransactionDetails(newPage);
    }
  };

  const exportToCSV = () => {
    if (!data || !data.transactions) return;

    const csvData = buildTransactionsCsv(data.transactions, {
      type,
      bfcfVariant,
      formatDate,
      formatTime,
      loadStatuses: showLoadStatus ? loadStatuses : null,
      engine: isEngineList,
    });

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = bfcfVariant
      ? `${bfcfVariant}-${bfcf!.startDate}-to-${bfcf!.endDate}.csv`
      : `${type}-transactions-${date}-page-${currentPage}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateString: string | null | undefined) => {
    const date = new Date(dateString as string);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: MINING_TZ,
    });
  };

  const formatTime = (dateString: string | null | undefined) => {
    const date = new Date(dateString as string);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: MINING_TZ,
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-2xl shadow-xl max-w-7xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              {bfcfVariant
                ? LIST_TEXT[bfcfVariant].title
                : `${type === 'dispatch' ? 'Dispatch' : 'Receipt'} Transactions`}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {groupingName} -{' '}
              {bfcfVariant
                ? `${formatDate(bfcf!.startDate)} to ${formatDate(bfcf!.endDate)}`
                : formatDate(date)}
            </p>
            {bfcfVariant && (
              <p className="text-xs text-gray-500 mt-1">
                {(isEngineList && LIST_TEXT[bfcfVariant].engine) || LIST_TEXT[bfcfVariant].legacy}
              </p>
            )}
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <FaSpinner className="animate-spin w-8 h-8 text-primary-500 mx-auto mb-4" />
                <p className="text-gray-600">Loading transaction details...</p>
                <button
                  onClick={handleCancel}
                  className="mt-4 px-4 py-2 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-red-600 mb-4">{error}</p>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {data && !loading && !error && (
            <>
              {/* Summary Stats */}
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Total Transactions</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {data.summary.totalCount.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Total Weight</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {data.summary.totalWeight.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      tonnes
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Average Weight</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {data.summary.avgWeight.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      tonnes
                    </p>
                  </div>
                  {type === 'receipt' && data.summary.avgVariance !== null && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Average Variance</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {data.summary.avgVariance!.toFixed(2)}%
                      </p>
                    </div>
                  )}
                  {isCarriedForward && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Receipted After Period</p>
                      <p
                        className={`text-lg font-semibold ${(data.summary.lateReceiptCount || 0) > 0 ? 'text-amber-700' : 'text-gray-900'}`}
                      >
                        {(data.summary.lateReceiptCount || 0).toLocaleString()}
                      </p>
                      <p className="text-[10px] text-gray-500">
                        within {data.lateReceiptWindowDays || 30} days of period end
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Controls Bar */}
              <div className="px-6 py-3 border-b border-gray-200 flex justify-between items-center">
                <div className="relative">
                  <button
                    onClick={() => setShowColumnSelector(!showColumnSelector)}
                    className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors"
                  >
                    <FaCog className="mr-2" />
                    Columns
                  </button>

                  {/* Column Selector Dropdown */}
                  {showColumnSelector && (
                    <div className="absolute top-full left-0 mt-2 w-64 bg-surface border border-gray-200 rounded-lg shadow-lg z-10">
                      <div className="p-4">
                        <div className="flex justify-between items-center mb-3">
                          <h3 className="text-sm font-medium text-gray-900">Show Columns</h3>
                          <button
                            onClick={resetToDefaults}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Reset to Defaults
                          </button>
                        </div>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {visibleColumns.map((column) => (
                            <label key={column.key} className="flex items-center">
                              <input
                                type="checkbox"
                                checked={column.visible}
                                onChange={() => toggleColumn(column.key)}
                                className="mr-2"
                              />
                              <span className="text-sm text-gray-700">{column.label}</span>
                              {column.visible ? (
                                <FaEye className="ml-auto text-green-500 text-xs" />
                              ) : (
                                <FaEyeSlash className="ml-auto text-gray-400 text-xs" />
                              )}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  onClick={exportToCSV}
                  className="flex items-center px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                >
                  <FaFileExport className="mr-2" />
                  Export CSV
                </button>
              </div>

              {/* Pagination Info */}
              {data.pagination && (
                <div className="px-6 py-3 bg-blue-50 border-b border-blue-200 flex justify-between items-center">
                  <div className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-semibold">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                    <span className="font-semibold">
                      {Math.min(currentPage * pageSize, data.pagination.totalRecords)}
                    </span>{' '}
                    of{' '}
                    <span className="font-semibold">
                      {data.pagination.totalRecords.toLocaleString()}
                    </span>{' '}
                    transactions
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={!data.pagination.hasPreviousPage}
                      className="px-3 py-1 bg-surface border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <FaChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm text-gray-700">
                      Page <span className="font-semibold">{currentPage}</span> of{' '}
                      <span className="font-semibold">{data.pagination.totalPages}</span>
                    </span>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!data.pagination.hasNextPage}
                      className="px-3 py-1 bg-surface border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <FaChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {data.truncated && (
                <div className="px-6 py-2 bg-amber-50 border-b border-amber-200 text-sm text-amber-800">
                  Showing the first {data.transactions.length.toLocaleString()} of{' '}
                  {data.summary.totalCount.toLocaleString()} loads. Narrow the date range to see the
                  rest.
                </div>
              )}

              {bfcfVariant && data.transactions.length === 0 && (
                <div className="px-6 py-10 text-center text-gray-500 text-sm">
                  No loads to show — the summary may have been generated before the latest data
                  load.
                </div>
              )}

              {/* Transactions Table */}
              <div
                className="flex-1 overflow-auto relative"
                style={{ maxHeight: 'calc(90vh - 400px)' }}
              >
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      {getVisibleColumnsOnly().map((column) => (
                        <th
                          key={column.key}
                          className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                            column.key === 'nettWeight' || column.key === 'variancePercent'
                              ? 'text-right'
                              : 'text-left'
                          }`}
                        >
                          {column.key === 'nettWeight' ? (
                            <span title="Click on weight values to select and sum them">
                              {column.label}
                            </span>
                          ) : (
                            column.label
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-surface divide-y divide-gray-200">
                    {data.transactions.map((transaction, index) => (
                      <tr
                        key={transaction.ticketNo}
                        className={index % 2 === 0 ? 'bg-surface' : 'bg-gray-50'}
                      >
                        {getVisibleColumnsOnly().map((column) => (
                          <td
                            key={column.key}
                            className={`px-4 py-3 whitespace-nowrap text-sm ${
                              column.key === 'nettWeight'
                                ? `text-right cursor-pointer select-none transition-colors duration-150 ${
                                    selectedWeightIndices.has(index)
                                      ? 'bg-blue-100 text-blue-900 font-bold'
                                      : 'text-gray-900 hover:bg-blue-50'
                                  }`
                                : column.key === 'variancePercent'
                                  ? 'text-right text-gray-900'
                                  : 'text-left text-gray-900'
                            }`}
                            onClick={
                              column.key === 'nettWeight'
                                ? (e) => handleWeightCellClick(e, index)
                                : undefined
                            }
                            title={
                              column.key === 'nettWeight'
                                ? 'Click to select for sum calculation'
                                : undefined
                            }
                          >
                            {renderCellContent(transaction, column)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Fixed-position sum calculator */}
                {selectedWeightIndices.size > 0 && (
                  <div
                    className="fixed bottom-5 left-1/2 z-[60]"
                    style={{
                      transform: 'translateX(-50%)',
                      animation: 'modalWeightCalcFadeIn 200ms ease-out',
                    }}
                  >
                    <style>{`
                      @keyframes modalWeightCalcFadeIn {
                        from { opacity: 0; transform: translateX(-50%) translateY(12px); }
                        to { opacity: 1; transform: translateX(-50%) translateY(0); }
                      }
                    `}</style>
                    <div className="inline-flex items-center gap-3 bg-slate-900 dark:bg-slate-50 text-white pl-4 pr-2 py-2.5 rounded-xl shadow-2xl shadow-slate-900/40 dark:shadow-black/40 border border-slate-700 dark:border-slate-200">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400 text-xs font-medium">
                          {selectedWeightIndices.size} selected
                        </span>
                        <span className="text-slate-600">|</span>
                        <span className="text-xs text-slate-400">Sum:</span>
                        <span className="text-sm font-bold text-white tabular-nums">
                          {selectedWeightSum.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                        <span className="text-xs text-slate-400">t</span>
                      </div>
                      <button
                        onClick={clearWeightSelection}
                        className="ml-1 p-1.5 hover:bg-slate-700 rounded-lg transition-colors text-slate-400 hover:text-white"
                        title="Clear selection"
                      >
                        <FaTimes className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!loading && (
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionDetailsModal;
